import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import "./AgricultureLivelihood.css";
import "./ProgramsBanner.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

// Simple inline icon components (no external icon-font dependency)
const IconLeaf = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M5 21c8 0 14-6 14-14V5h-2C9 5 3 11 3 19v2h2z" />
    <path d="M5 21c4-6 8-9 14-12" />
  </svg>
);

const IconUsers = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.5 20c0-3.6 2.9-6.2 6.5-6.2s6.5 2.6 6.5 6.2" />
    <circle cx="17.5" cy="8.6" r="2.4" />
    <path d="M15.7 13.9c2.7.3 4.8 2.5 4.8 5.6" />
  </svg>
);

const IconSprout = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M12 22v-9" />
    <path d="M12 13c0-4 3-6 7-6 0 4-3 6-7 6z" />
    <path d="M12 13C12 9 9 7 5 7c0 4 3 6 7 6z" />
  </svg>
);

const IconChicken = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M8 10c0-3 2.2-5 5-5s5 2 5 5" />
    <circle cx="15.5" cy="7" r="0.6" fill="#1f9d55" />
    <path d="M6 14c0-2.2 2-4 6-4s6 1.8 6 4-2 5-6 5-6-2.8-6-5z" />
    <path d="M9 19l-1 2M15 19l1 2" />
  </svg>
);

const IconFish = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M3 12c3-4 8-6 12-6 3.5 3.5 3.5 8.5 0 12-4 0-9-2-12-6z" />
    <path d="M21 8l-3 4 3 4" />
    <circle cx="8" cy="11" r="0.6" fill="#1f9d55" />
  </svg>
);

const IconRoot = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M12 3c2 2 2 5 0 7-2-2-2-5 0-7z" />
    <path d="M12 10v11" />
    <path d="M12 14c-2 0-3 2-3 4M12 17c1.5 0 2.5 1.5 2.5 3" />
  </svg>
);

const IconHeart = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" stroke="none">
    <path d="M12 21s-7.5-4.6-10-9.3C.4 8.6 2 5 5.6 5c2 0 3.4 1.1 4.4 2.6C11 6.1 12.4 5 14.4 5 18 5 19.6 8.6 22 11.7 19.5 16.4 12 21 12 21z" />
  </svg>
);

const IconUserPlus = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.5 20c0-3.6 2.9-6.2 6.5-6.2s6.5 2.6 6.5 6.2" />
    <path d="M18 8v5M20.5 10.5h-5" />
  </svg>
);

const solutions = [
  {
    icon: <IconSprout />,
    title: "Improved Kitchen Gardens",
    text: "Promoting nutrition and self-sufficiency at home.",
  },
  {
    icon: <IconChicken />,
    title: "Backyard Poultry & Goat/Duck Farms",
    text: "Encouraging small-scale livestock for steady income.",
  },
  {
    icon: <IconFish />,
    title: "Aqua-Culture Projects",
    text: "Sustainable fish farming for food security and income.",
  },
  {
    icon: <IconRoot />,
    title: "Root & Tuber Crop Cultivation",
    text: "Utilizing local resources for better productivity.",
  },
  {
    icon: <IconLeaf />,
    title: "Off-Season Vegetable Cultivation",
    text: "Year-round cultivation for increased returns.",
  },
  {
    icon: <IconSprout />,
    title: "Sapling Growth & Marketing",
    text: "Nurturing plants and creating market linkages.",
  },
];

// Renamed component to better reflect its content
const AgricultureLivelihood = () => {
  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="page-header page-header--abl">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/agriculture_banner_1521x482.png)", // Make sure this path is correct
          }}
        ></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <Container>
          <div className="page-header__inner text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Agriculture Based Livelihood
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ color: "#dad2d0", fontSize: "22px", marginTop: "20px" }}
            >
              Fostering sustainable income through a blend of indigenous
              agriculture and modern technology.
            </motion.p>
          </div>
        </Container>
      </section>

      {/* Introduction Section */}
      <section className="py-5 abl-approach-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center justify-content-center g-5">
              <motion.div className="col-md-5" variants={staggerItem}>
                <span className="abl-eyebrow">
                  <IconLeaf /> Our Approach
                </span>
                <h2 className="fw-bold mb-3 abl-heading" style={{ color: "#006633" }}>
                  Our Approach to Sustainable Farming
                </h2>
                <div className="abl-heading-underline"></div>
                <p className="lh-lg">
                  Our focus is on generating sustainable income by integrating
                  indigenous agricultural concepts with modern mechanisms. We
                  advocate for new technologies through comprehensive training,
                  practical demonstrations, and educational exposure visits for
                  local farmers.
                </p>
              </motion.div>

              <motion.div className="col-md-6" variants={staggerItem}>
                <Row className="g-3">
                  <Col xs={6}>
                    <div className="abl-photo-card">
                      <img
                        src="/assets/images/Subsections/Abl1.jpg"
                        alt="Farmer training"
                        className="img-fluid rounded-5 shadow"
                      />
                      <div className="abl-photo-badge">
                        <IconLeaf />
                      </div>
                      <h4>Sustainable Practices</h4>
                      <p>Environment-friendly farming for a better tomorrow.</p>
                    </div>
                  </Col>

                  <Col xs={6}>
                    <div className="abl-photo-card">
                      <img
                        src="/assets/images/Subsections/abl2.jpg"
                        alt="Crop cultivation"
                        className="img-fluid rounded-5 shadow"
                      />
                      <div className="abl-photo-badge">
                        <IconUsers />
                      </div>
                      <h4>Empowering Communities</h4>
                      <p>Building skills, knowledge and stronger rural communities.</p>
                    </div>
                  </Col>
                </Row>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Key Solutions Section */}
      <section className="py-5 bg-light abl-solutions-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center mb-5"
          >
            <span className="abl-eyebrow abl-eyebrow--center">
              <IconLeaf /> Our Solutions
            </span>
            <h2 className="fw-bold abl-heading" style={{ color: "#006633" }}>
              Our Agro-Based Solutions
            </h2>
            <p className="lead abl-subtext">
              We have developed various solutions to enhance livelihood at the
              cluster level.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4">
              {solutions.map((item, idx) => (
                <Col md={4} key={idx}>
                  <motion.div variants={staggerItem} className="h-100">
                    <Card className="text-center p-4 border-0 shadow-sm h-100 abl-solution-card">
                      <div className="abl-solution-icon">{item.icon}</div>
                      <h5>{item.title}</h5>
                      <p>{item.text}</p>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5 abl-cta-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div className="abl-cta-box">
              <div className="abl-cta-content">
                <div className="abl-cta-icon">
                  <IconHeart />
                </div>
                <h3>Let's Grow Together</h3>
                <p>
                  Empower rural communities and build a sustainable future
                  through agriculture-based livelihoods.
                </p>
                <div className="abl-cta-buttons">
                  <Button className="abl-btn abl-btn--solid">
                    <IconHeart /> Donate Now
                  </Button>
                  <Button variant="outline-light" className="abl-btn abl-btn--outline">
                    <IconUserPlus /> Join Us
                  </Button>
                </div>
              </div>
              <div className="abl-cta-image">
                <img
                  src="/assets/images/backgrounds/do1.png"
                  alt="Growing together"
                />
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default AgricultureLivelihood;